import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Chip, 
  Button, 
  Avatar, 
  LinearProgress,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LanguageIcon from '@mui/icons-material/Language';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import InfoIcon from '@mui/icons-material/Info';
import { importExportService, MarketOpportunity } from '../../services/importExportService';

const OpportunityCard = ({ country, commodity, price, currency, growth, demand, description }: MarketOpportunity) => {
  const theme = useTheme();
  return (
    <Card sx={{ borderRadius: 3, height: '100%', border: `1px solid ${theme.palette.divider}`, boxShadow: 'none' }}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
          <Box display="flex" gap={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>{country.charAt(0)}</Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">{country}</Typography>
              <Typography variant="body2" color="text.secondary">Target Market</Typography>
            </Box>
          </Box>
          <Chip 
            label={`+${growth.toFixed(1)}% Growth`} 
            size="small" 
            color="success" 
            sx={{ fontWeight: 'bold' }} 
          />
        </Box>
        
        <Box mb={2}>
          <Typography variant="h5" fontWeight="800" color="primary.main">
            {commodity}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current Market Price: <b>{currency}{price}</b> / kg
          </Typography>
        </Box>

        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" fontWeight="bold">Demand Intensity</Typography>
            <Typography variant="caption" color={demand > 80 ? "error.main" : "warning.main"}>
              {demand > 80 ? "High" : "Medium"}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={demand} 
            color={demand > 80 ? "error" : "warning"} 
            sx={{ borderRadius: 2, height: 6 }} 
          />
        </Box>

        <Box sx={{ mb: 2, flexGrow: 1 }}>
          <Box display="flex" gap={1} alignItems="flex-start">
            <InfoIcon color="disabled" sx={{ fontSize: 16, mt: 0.3 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {description}
            </Typography>
          </Box>
        </Box>

        <Button variant="outlined" fullWidth startIcon={<FlightTakeoffIcon />} size="small">
          View Export Requirements
        </Button>
      </CardContent>
    </Card>
  );
};

const MarketOpportunities: React.FC = () => {
  const theme = useTheme();
  const [opportunities, setOpportunities] = useState<MarketOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        setLoading(true);
        const data = await importExportService.getMarketOpportunities();
        setOpportunities(data);
      } catch (err) {
        console.error('Failed to fetch market opportunities:', err);
        setError('Failed to load global market opportunities.');
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunities();
  }, []);

  return (
    <Box>
      <Box mb={4} textAlign="center">
        <Typography variant="h4" fontWeight="800" gutterBottom>
          Global Market Opportunities
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" maxWidth="md" mx="auto">
          Real-world export destinations based on UN Comtrade global import demand and price arbitrage analysis.
        </Typography>
      </Box>

      {error && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={2}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">Analyzing global trade patterns...</Typography>
        </Box>
      ) : (
        <Grid container spacing={3} mb={4}>
          {opportunities.map((opp, index) => (
            <Grid item xs={12} md={4} lg={3} key={`${opp.hsCode}-${index}`}>
              <OpportunityCard {...opp} />
            </Grid>
          ))}
        </Grid>
      )}

      <Card sx={{ bgcolor: theme.palette.primary.main, color: 'white', borderRadius: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <MonetizationOnIcon fontSize="large" />
                <Typography variant="h5" fontWeight="bold">Premium Export Program</Typography>
              </Box>
              <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
                Join our aggregated export scheme to access bulk shipping rates and guaranteed payment upon delivery. 
                We handle the logistics and compliance for you.
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Chip icon={<VerifiedUserIcon sx={{ color: 'white !important' }} />} label="Guaranteed Payment" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                <Chip icon={<LanguageIcon sx={{ color: 'white !important' }} />} label="Global Reach" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
              </Box>
            </Grid>
            <Grid item xs={12} md={4} textAlign="center">
              <Button 
                variant="contained" 
                size="large" 
                sx={{ 
                  bgcolor: 'white', 
                  color: 'primary.main', 
                  fontWeight: 'bold',
                  px: 4,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } 
                }}
              >
                Start Exporting
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MarketOpportunities;
